import React, { useState, useEffect } from "react";

import QuizGoogleSheet from "./QuizGoogleSheet"
import "./quizlayout.css";

export default function QuizLoader() {
  const subjects = [
    {
      id: "crp",
      title: "The Code of Criminal Procedure, 1898",
      subtitle: "Criminal procedure, powers of courts, investigation & trial",
      chapters: 38,
      color: "#2563eb",
      icon: "⚖️",
    },
    {
      id: "cpc",
      title: "The Code of Civil Procedure, 1908",
      subtitle: "Civil suits, decrees, orders and execution",
      chapters: 51,
      color: "#10b981",
      icon: "📘",
    },
    {
      id: "tax",
      title: "Income Tax Act, 2023",
      subtitle: "Assessment, TDS, penalties and appeals",
      chapters: 25,
      color: "#f59e0b",
      icon: "💰",
    },
    {
      id: "professionalethics",
      title: "Professional Ethics & Bar Council Order",
      subtitle: "Advocate conduct, discipline & professional standards",
      chapters: 12,
      color: "#ec4899",
      icon: "🧑‍⚖️",
    },
    {
      id: "penalcode",
      title: "Penal Code, 1860",
      subtitle: "Definitions, offences, punishment and general exceptions",
      chapters: 23,
      color: "#ef4444",
      icon: "🚨",
    },
    {
      id: "theevidenceact",
      title: "The Evidence Act, 1872",
      subtitle: "Relevancy, admissibility and burden of proof",
      chapters: 11,
      color: "#8b5cf6",
      icon: "📑",
    },
    {
      id: "thelimitionact",
      title: "The Limitation Act",
      subtitle: "Time limits for suits, appeals and applications",
      chapters: 10,
      color: "#0ea5e9",
      icon: "⏳",
    },
    {
      id: "specificreliefact",
      title: "The Specific Relief Act, 1877",
      subtitle: "Specific performance, injunctions and declaratory relief",
      chapters: 8,
      color: "#14b8a6",
      icon: "📝",
    },
    {
      id: "slybus",
      title: "Complete Syllabus Overview",
      subtitle: "All subjects combined syllabus wise preparation",
      chapters: 9,
      color: "#6b7280",
      icon: "📚",
    },
  ];

  const [active, setActive] = useState(subjects[0].id);
  const [darkMode, setDarkMode] = useState(false);

  // 🕒 Exam timer (seconds)
  const EXAM_DURATION = 30 * 60; // 30 minutes
  const [timeLeft, setTimeLeft] = useState(EXAM_DURATION);

  // 🧮 Scoreboard state (you’ll wire this from QuizGoogleSheet later)
  const [score, setScore] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [attempted, setAttempted] = useState(0);

  const info = subjects.find((s) => s.id === active) || subjects[0];

  // Reset timer and score when subject changes
  useEffect(() => {
    setTimeLeft(EXAM_DURATION);
    setScore(0);
    setTotalQuestions(0);
    setAttempted(0);
  }, [active]);

  // Timer countdown
  useEffect(() => {
    if (timeLeft <= 0) return;
    const t = setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
    return () => clearInterval(t);
  }, [timeLeft]);

  const formatTime = (sec) => {
    const m = String(Math.floor(sec / 60)).padStart(2, "0");
    const s = String(sec % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  const accuracy =
    totalQuestions > 0 ? Math.round((score / totalQuestions) * 100) : 0;

  return (
    <div className={`layout-root ${darkMode ? "dark" : ""}`}>
      {/* TOP BAR – Dark mode + Subject quick info */}
      <header className="topbar">
        <div className="topbar-left">
          <span className="logo-mark">MCQ</span>
          <div>
            <div className="topbar-title">Judicial MCQ Practice</div>
            <div className="topbar-subtitle">
              Subject wise practice with live timer and score
            </div>
          </div>
        </div>

        <div className="topbar-right">
          <button
            className="dark-toggle"
            onClick={() => setDarkMode((d) => !d)}
          >
            {darkMode ? "🌙 Dark" : "☀️ Light"}
          </button>
        </div>
      </header>

      <div className="layout-container">
        {/* LEFT SIDEBAR (Desktop) */}
        <aside className="sidebar">
          {subjects.map((s) => (
            <button
              key={s.id}
              className={`sidebar-btn ${active === s.id ? "active" : ""}`}
              onClick={() => setActive(s.id)}
              style={{
                borderLeftColor: active === s.id ? s.color : "transparent",
              }}
            >
              <span className="sidebar-icon">{s.icon}</span>
              <span className="sidebar-text">{s.title}</span>
            </button>
          ))}
        </aside>

        {/* MOBILE DROPDOWN */}
        <div className="mobile-dropdown">
          <select
            value={active}
            onChange={(e) => setActive(e.target.value)}
          >
            {subjects.map((s) => (
              <option key={s.id} value={s.id}>
                {s.icon} {s.title}
              </option>
            ))}
          </select>
        </div>

        {/* TABLET SLIDER */}
        <div className="tab-slider">
          {subjects.map((s) => (
            <button
              key={s.id}
              className={`slider-btn ${active === s.id ? "active" : ""}`}
              onClick={() => setActive(s.id)}
              style={{
                background: active === s.id ? s.color : "#ddd",
                color: active === s.id ? "#fff" : "#000",
              }}
            >
              <span className="slider-icon">{s.icon}</span> {s.title}
            </button>
          ))}
        </div>

        {/* MAIN CONTENT */}
        <main className="content-area">
          {/* EXAM HEADER */}
          <div className="exam-header">
            <div className="exam-title-block">
              <h1 style={{ color: info.color }}>
                {info.icon} {info.title}
              </h1>
              <p className="subject-subtitle">{info.subtitle}</p>
              <div className="subject-meta">
                <span>Chapters: {info.chapters}</span>
                <span>Sheet: {active}</span>
              </div>
            </div>

            {/* TIMER + SCOREBOARD */}
            <div className="exam-meta-card">
              <div className="timer-row">
                <span className="meta-label">Time Left</span>
                <span className="meta-value">
                  {timeLeft > 0 ? formatTime(timeLeft) : "00:00"}
                </span>
              </div>
              <div className="score-row">
                <span className="meta-label">Score</span>
                <span className="meta-value">
                  {score} / {totalQuestions || "—"}
                </span>
              </div>
              <div className="score-row">
                <span className="meta-label">Attempted</span>
                <span className="meta-value">
                  {attempted} / {totalQuestions || "—"}
                </span>
              </div>
              <div className="score-row">
                <span className="meta-label">Accuracy</span>
                <span className="meta-value">
                  {totalQuestions ? `${accuracy}%` : "—"}
                </span>
              </div>
            </div>
          </div>

          {/* QUIZ COMPONENT */}
          <QuizGoogleSheet
            sheetName={active}
            // 👇 এগুলো আপনি QuizGoogleSheet এ implement করতে পারবেন
            // onMetaChange={({ total }) => setTotalQuestions(total)}
            // onProgressChange={({ attemptedCount }) => setAttempted(attemptedCount)}
            // onScoreChange={(newScore) => setScore(newScore)}
          />
        </main>
      </div>
    </div>
  );
}
